import { z } from 'zod';

const vadStartSensitivitySchema = z.enum([
  'START_SENSITIVITY_LOW',
  'START_SENSITIVITY_MEDIUM',
  'START_SENSITIVITY_HIGH',
]);

const vadEndSensitivitySchema = z.enum([
  'END_SENSITIVITY_LOW',
  'END_SENSITIVITY_MEDIUM',
  'END_SENSITIVITY_HIGH',
]);

const thinkingLevelSchema = z.enum(['minimal', 'low', 'medium', 'high']);

/**
 * How custom-tool function responses are scheduled. Consumed by
 * live-api.service.ts, which maps these onto genai's FunctionResponseScheduling.
 */
const toolResponseSchedulingSchema = z.enum(['interrupt', 'when_idle', 'silent']);

export const advancedConfigurationSchema = z.object({
  model: z.string().min(1).optional(),
  voice_name: z.string().min(1).max(100).optional(),
  temperature: z.number().min(0).max(2).optional(),
  language_code: z
    .string()
    .regex(/^[a-z]{2}-[A-Z]{2}$/, 'Language must be in BCP-47 format like en-GB')
    .optional(),
  thinking_level: thinkingLevelSchema.optional(),
  thinking_budget: z.number().int().min(0).max(32768).optional(),
  include_thoughts: z.boolean().optional(),
  vad_disabled: z.boolean().optional(),
  vad_start_sensitivity: vadStartSensitivitySchema.optional(),
  vad_end_sensitivity: vadEndSensitivitySchema.optional(),
  prefix_padding_ms: z.number().int().min(0).max(5000).optional(),
  silence_duration_ms: z.number().int().min(0).max(10000).optional(),
  enable_affective_dialog: z.boolean().optional(),
  proactive_audio: z.boolean().optional(),
  compression_disabled: z.boolean().optional(),
  trigger_tokens: z.number().int().min(1).max(1_000_000).optional(),
  target_tokens: z.number().int().min(1).max(1_000_000).optional(),
  silence_breaker: z
    .object({
      enabled: z.boolean(),
      timeout_ms: z.number().int().min(2000).max(30000),
      max_attempts: z.number().int().min(1).max(10),
    })
    .optional(),
  grounding_enabled: z.boolean().optional(),
  tool_response_scheduling: toolResponseSchedulingSchema.optional(),
  // When true, attach the project's Vertex RAG corpus so the agent grounds its
  // answers on uploaded knowledge-base files. Set by the RAG section of the
  // configuration form when files are present.
  rag_enabled: z.boolean().optional(),
  // Which engine runs voice calls. 'genai' (default) is the single-session
  // Live API engine in live-api.service.ts; 'adk' runs the call through the
  // ADK runner (one Live session per agent, native transfers). Experimental.
  voice_engine: z.enum(['genai', 'adk']).optional(),
});

export type AdvancedConfiguration = z.infer<typeof advancedConfigurationSchema>;
